import { fmtDate } from "@/lib/utils";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Package, FileText, Bell, Star, ArrowRight, MapPin, CheckCircle2, FileDown, XCircle, AlertTriangle } from "lucide-react";
import { ClaimModal, ClaimBadge } from "@/components/DamageClaim";
import { CancelModal } from "@/components/CancelModal";
import { BookingChat } from "@/components/BookingChat";
import { ProofView } from "@/components/ProofOfDelivery";
import { photoUrl } from "@/components/PhotoUpload";

const STATUS_COLORS = {
  reserviert: "bg-amber-500/15 text-amber-400", freigegeben: "bg-sky-500/15 text-sky-300", bestaetigt: "bg-emerald-500/15 text-emerald-400",
  abgeschlossen: "bg-blue-500/15 text-blue-400", storniert: "bg-red-500/15 text-red-400", abgelehnt: "bg-red-500/15 text-red-400", abgelaufen: "bg-red-500/15 text-red-400",
};
const STATUS_LABEL = { reserviert: "Partner prüft – Bestätigung ausstehend", freigegeben: "Bestätigt – jetzt bezahlen", bestaetigt: "Bezahlt · verbindlich", abgelehnt: "Vom Partner abgelehnt", abgelaufen: "Frist abgelaufen" };

export default function CustomerDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("bookings");
  const [requests, setRequests] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [review, setReview] = useState(null);
  const [cancel, setCancel] = useState(null);
  const [claim, setClaim] = useState(null);

  const load = async () => {
    const [r, b, n] = await Promise.all([
      api.get("/requests/mine"), api.get("/bookings/mine"), api.get("/notifications"),
    ]);
    setRequests(r.data); setBookings(b.data); setNotifs(n.data);
  };
  useEffect(() => { load(); }, []);

  const submitReview = async (rating, text) => {
    try {
      await api.post("/reviews", { booking_id: review.id, rating, text });
      toast.success("Bewertung abgegeben – wird geprüft.");
      setReview(null);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const TABS = [
    ["bookings", "Buchungen", Package], ["requests", "Anfragen", FileText], ["notifs", "Nachrichten", Bell],
  ];

  return (
    <div className="eb-container py-12">
      <h1 className="font-head text-3xl font-bold mb-1">Hallo, {user?.name?.split(" ")[0]}</h1>
      <p className="text-slate-400 mb-8">Deine Anfragen, Buchungen und Nachrichten im Überblick.</p>

      <div className="flex gap-2 mb-6 border-b border-slate-800 overflow-x-auto whitespace-nowrap">
        {TABS.map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`cust-tab-${k}`}
            className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors shrink-0 ${tab===k?"border-[#FF5A1F] text-white":"border-transparent text-slate-400 hover:text-slate-200"}`}>
            <Icon className="w-4 h-4" /> {l}
          </button>
        ))}
      </div>

      {tab === "bookings" && (
        <div className="space-y-4" data-testid="cust-bookings">
          {bookings.length === 0 ? <Empty text="Noch keine Buchungen." /> : bookings.map((b) => (
            <div key={b.id} className="eb-card p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 font-head font-semibold"><MapPin className="w-4 h-4 text-[#FF5A1F]" /> {b.route_label}</div>
                  <div className="text-sm text-slate-400 mt-1">Abfahrt {fmtDate(b.abfahrt)} · {b.partner_name}</div>
                  {["bestaetigt", "abgeschlossen"].includes(b.status) && (
                    <div className="flex items-center gap-1.5 mt-3" data-testid={`cust-transport-status-${b.id}`}>
                      {["geplant", "unterwegs", "zugestellt"].map((s, i) => {
                        const idx = ["geplant", "unterwegs", "zugestellt"].indexOf(b.transport_status || "geplant");
                        const done = i <= idx;
                        return <span key={s} className="flex items-center gap-1.5"><span className={`text-xs px-2 py-0.5 rounded-full ${done ? (s === "zugestellt" && i === idx ? "bg-emerald-500/20 text-emerald-300" : "bg-[#FF5A1F]/20 text-[#FF8A5F]") : "bg-slate-800 text-slate-500"}`}>{s}</span>{i < 2 && <span className={`w-5 h-px ${i < idx ? "bg-[#FF5A1F]" : "bg-slate-700"}`} />}</span>;
                      })}
                    </div>
                  )}
                  <ProofView proof={b.proof} photoUrl={photoUrl} bookingId={b.id} />
                  {b.claim_status && <div className="mt-2"><ClaimBadge status={b.claim_status} /></div>}
                </div>
                <div className="text-right">
                  <span className={`eb-badge ${STATUS_COLORS[b.status] || "bg-slate-700 text-slate-300"}`} data-testid={`cust-booking-status-${b.id}`}>{STATUS_LABEL[b.status] || b.status}</span>
                {["abgelehnt", "abgelaufen"].includes(b.status) && <Link to={`/angebote/${b.request_id}`} className="eb-btn-outline !py-2 text-sm" data-testid={`cust-rechoose-${b.id}`}>Andere Route wählen</Link>}
                  <div className="font-head text-xl font-bold text-[#FF5A1F] mt-1">{b.customer_price} €</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-800">
                {b.status === "freigegeben" && <Link to={`/checkout/${b.id}`} className="eb-btn-primary !py-2 text-sm" data-testid={`cust-pay-${b.id}`}>Zahlung abschließen <ArrowRight className="w-4 h-4" /></Link>}
                {b.payment_status === "paid" && <span className="eb-badge bg-emerald-500/15 text-emerald-400"><CheckCircle2 className="w-3.5 h-3.5" /> bezahlt</span>}
                {b.payment_status === "paid" && <a href={`${API}/bookings/${b.id}/invoice`} target="_blank" rel="noreferrer" className="eb-btn-outline !py-2 text-sm" data-testid={`cust-invoice-${b.id}`}><FileDown className="w-4 h-4" /> Rechnung{b.invoice_number ? ` ${b.invoice_number}` : ""}</a>}
                {b.payment_status === "paid" && <button onClick={() => setReview(b)} className="eb-btn-outline !py-2 text-sm" data-testid={`cust-review-${b.id}`}><Star className="w-4 h-4" /> Bewerten</button>}
                {["reserviert", "freigegeben", "bestaetigt"].includes(b.status) && <button onClick={() => setCancel(b)} className="eb-btn-outline !py-2 text-sm !border-red-500/40 !text-red-300 hover:!bg-red-500/10" data-testid={`cust-cancel-${b.id}`}><XCircle className="w-4 h-4" /> Stornieren</button>}
                {b.status === "storniert" && b.refund?.status === "refunded" && <span className="text-xs text-slate-400">Rückerstattung {b.refund.amount} € via {b.refund.provider === "paypal" ? "PayPal" : "Stripe"} veranlasst</span>}
                {(b.transport_status === "zugestellt" || b.status === "abgeschlossen") && !b.claim_status && <button onClick={() => setClaim(b)} className="eb-btn-outline !py-2 text-sm !border-amber-500/40 !text-amber-300 hover:!bg-amber-500/10" data-testid={`cust-claim-${b.id}`}><AlertTriangle className="w-4 h-4" /> Schaden melden</button>}
              </div>
              <BookingChat bookingId={b.id} meId={user?.id} />
            </div>
          ))}
        </div>
      )}

      {tab === "requests" && (
        <div className="space-y-4" data-testid="cust-requests">
          {requests.length === 0 ? <Empty text="Noch keine Anfragen." /> : requests.map((r) => (
            <div key={r.id} className="eb-card p-6 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 font-head font-semibold">{r.route_label}</div>
                <div className="text-sm text-slate-400 mt-1">{r.volume_m3} m³ · {r.cargo_type} · {r.flexible ? "flexibel" : `${fmtDate(r.date_from) || "–"}`}</div>
              </div>
              <Link to={`/angebote/${r.id}`} className="eb-btn-outline !py-2 text-sm" data-testid={`cust-view-offers-${r.id}`}>Angebote ansehen <ArrowRight className="w-4 h-4" /></Link>
            </div>
          ))}
        </div>
      )}

      {tab === "notifs" && (
        <div className="space-y-3" data-testid="cust-notifs">
          {notifs.length === 0 ? <Empty text="Keine Nachrichten." /> : notifs.map((n) => (
            <div key={n.id} className="eb-card p-4 flex items-start gap-3">
              <Bell className="w-4 h-4 text-[#FF5A1F] mt-1" />
              <div><div className="font-semibold text-slate-100">{n.title}</div><div className="text-sm text-slate-400">{n.message}</div></div>
            </div>
          ))}
        </div>
      )}

      {review && <ReviewModal onClose={() => setReview(null)} onSubmit={submitReview} booking={review} />}
      {cancel && <CancelModal booking={cancel} onClose={() => setCancel(null)} onDone={() => { setCancel(null); load(); }} />}
      {claim && <ClaimModal booking={claim} onClose={() => setClaim(null)} onDone={() => { setClaim(null); load(); }} />}
    </div>
  );
}

function Empty({ text }) { return <div className="eb-card p-10 text-center text-slate-400">{text}</div>; }

function ReviewModal({ onClose, onSubmit, booking }) {
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="eb-card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="review-modal">
        <h3 className="font-head text-lg font-semibold mb-1">Transport bewerten</h3>
        <p className="text-sm text-slate-400 mb-4">{booking.route_label} · {booking.partner_name}</p>
        <div className="flex gap-1 mb-4">
          {[1,2,3,4,5].map((s) => (
            <button key={s} onClick={() => setRating(s)} data-testid={`review-star-${s}`}>
              <Star className={`w-8 h-8 ${s<=rating?"text-amber-400 fill-amber-400":"text-slate-600"}`} />
            </button>
          ))}
        </div>
        <textarea className="eb-input min-h-[100px]" placeholder="Deine Erfahrung (optional)" value={text} onChange={(e) => setText(e.target.value)} data-testid="review-text" />
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="eb-btn-outline flex-1">Abbrechen</button>
          <button onClick={() => onSubmit(rating, text)} className="eb-btn-primary flex-1" data-testid="review-submit">Absenden</button>
        </div>
      </div>
    </div>
  );
}
